import { DatePicker, Form, Input, Modal, Select, Switch } from "antd";
import { AppButton } from "components/button/AppButton";
import dayjs from "dayjs";
import { useCreateEducationHistory } from "features/settings/hooks/useCreateEducationHistory";
import {
  QUERY_KEY_FOR_SINGLE_EDUCATION_HISTORY,
  useGetSingleEducation,
} from "features/settings/hooks/useGetSingleEducation";
import { QUERY_KEY_FOR_TALENT_PROFILE } from "features/settings/hooks/useGetTalentProfile";
import { ICreateEducationHistory } from "features/settings/types";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { IdentifierProps } from "types";
import { openNotification } from "utils/notification";
import axios from "axios";
import { END_POINT } from "config/environment";
import { COURSES } from "constants/general";
import { useGetDegrees } from "../../../jobs/hooks/useGetDegrees";

const requiredRule = (fieldName: string) => ({
  required: true,
  message: `${fieldName} is required`,
});

export const AddEducationHistory = ({
  open,
  handleClose,
  id,
}: IdentifierProps) => {
  const [currentSchool, setCurrentSchool] = useState(false);
  const [form] = Form.useForm();
  const { token, userInfo } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useCreateEducationHistory();
  const { data } = useGetSingleEducation({
    id: id as number,
    token,
  });
  const { data: degreesData, isLoading: isLoadingDegrees } = useGetDegrees();
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const config = {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        };
        const response = await axios.get(
          `${END_POINT.BASE_URL}/v1/get-institutions`,
          config,
        );
        setSchools(response.data);
      } catch (error) {
        console.error("Failed to fetch schools:", error);
        openNotification({
          title: "Error",
          state: "error",
          description: "Failed to fetch schools. Please try again.",
          duration: 6,
        });
      }
    };
    fetchSchools();
  }, [token]);

  const disableFutureDates = (current: dayjs.Dayjs) => {
    return current && current.isAfter(dayjs().endOf("day"));
  };

  useEffect(() => {
    if (!data || !id) return;

    const parseDate = (dateString: string | null) => {
      if (!dateString) return null;
      if (dateString.match(/^\d{4}-\d{2}$/)) {
        return dayjs(dateString, "YYYY-MM");
      }
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dayjs(dateString, "YYYY-MM-DD");
      }
      return dayjs(dateString);
    };

    const isCurrent = data.is_current === 1;
    setCurrentSchool(isCurrent);

    let dateRange = undefined;
    let startDateOnly = undefined;

    if (data.start_date && data.end_date && !isCurrent) {
      dateRange = [parseDate(data.start_date), parseDate(data.end_date)];
    } else if (data.start_date && isCurrent) {
      startDateOnly = parseDate(data.start_date);
    } else if (data.start_date && data.end_date === null) {
      startDateOnly = parseDate(data.start_date);
    }

    const degreeExists = degreesData?.some(
      (degree: any) => degree.id === data?.degree_id,
    );

    const formValues = {
      institution: data?.school,
      is_current: isCurrent,
      course: data?.course,
      degree_id: degreeExists ? data?.degree_id : undefined,
      grade: data?.grade || undefined,
      date: dateRange,
      ss: startDateOnly,
    };

    form.setFieldsValue(formValues);
  }, [form, data, id, degreesData]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setCurrentSchool(false);
    }
  }, [open, form]);

  const handleSubmit = (formData: any) => {

    const startDate = formData.date ? formData.date[0].format("MM/YYYY") : null;
    const endDate = formData.date ? formData.date[1].format("MM/YYYY") : null;
    const onStartDate = formData.ss ? formData.ss.format("MM/YYYY") : null;

    const course = formData.course==='Others'?formData.customCourse:formData.course;

    const props: ICreateEducationHistory = {
      id: id as number,
      talent_id: userInfo?.talent?.id,
      school: formData.institution,
      start_date: currentSchool ? onStartDate : startDate,
      end_date: endDate,
      is_current: currentSchool,
      token,
      course: course,
      degree_id: formData.degree_id,
      grade: formData.grade,
    };

    mutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response && err.response.data && err.response.data.error) {
          errorTitle = "Error: " + err.response.data.error;
          errorDescription =
            err.response.data.description || "Please try again.";
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Education History Created Successfully",
          duration: 4.5,
        });
        form.resetFields();
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_TALENT_PROFILE],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_SINGLE_EDUCATION_HISTORY, id],
        });
        handleClose();
      },
    });
  };

  // Track form validation state for submit button
  const [isFormValid, setIsFormValid] = useState(false);

  const handleFieldsChange = () => {

    if(form.getFieldValue('customCourse') && form.getFieldValue('course')==='Others'){
      return;
    }
    form
      .validateFields()
      .then(() => setIsFormValid(true))
      .catch(() => setIsFormValid(false));
  };

  const degreeOptions =
    degreesData?.map((degree: any) => ({
      value: degree.id,
      label: degree.name,
    })) || [];

  return (
    <Modal
      title={id ? "Edit Education History" : "Add Education History"}
      open={open}
      onCancel={() => handleClose(false)}
      footer={null}
      width="40rem"
      style={{ top: 10 }}
    >
      <div>
        <Form
          layout="vertical"
          requiredMark={false}
          onFinish={handleSubmit}
          form={form}
          onValuesChange={handleFieldsChange}
          initialValues={{ is_current: false }}
        >
          <div className="flex items-center gap-2 mb-4 mt-5">
            <Form.Item noStyle name="is_current">
              <Switch size="small" onChange={(val) => setCurrentSchool(val)} />
            </Form.Item>
            <span>I currently attend this school</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5">
            <Form.Item
              name="institution"
              label="Institution"
              rules={[requiredRule("Institution")]}
              validateTrigger="onBlur"
            >
              <Select
                showSearch
                placeholder="Select Institution"
                optionFilterProp="children"
                filterOption={(input, option) => {
                  if (option && option.children) {
                    return String(option.children)
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              >
                {schools.map((school: any) => (
                  <Select.Option key={school.id} value={school.name}>
                    {school.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="course"
              label="Course"
              rules={[requiredRule("Course")]}
              validateTrigger="onBlur"
            >
              <Select
                options={[...COURSES, { label: "Others", value: "Others" }]}
                showSearch
                placeholder="Select Course"
                allowClear
                loading={COURSES.length === 0}
                filterOption={(input, option) => {
                  if (option?.value === "Others") {
                    return true;
                  }
                  return (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
              />
            </Form.Item>

            <Form.Item
                shouldUpdate={(prev, cur) => prev.course !== cur.course}
                noStyle
              >
                {({ getFieldValue }) => {
                  const selectedTitle = getFieldValue("course");
                  return selectedTitle === "Others" ? (
                    <Form.Item
                      name="customCourse"
                      label="Custom Course"
                      rules={[
                        {
                          required: true,
                          message: "Please enter a custom course",
                        },
                        {
                          pattern: /^[A-Za-z\s/&()-]+$/,
                          message: "Course should only contain letters",
                        },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const exists = COURSES.some(
                              (course: { value: string }) =>
                                course.value.toLowerCase() === value.toLowerCase(),
                            );
                            if (exists) {
                              return Promise.reject(
                                "This inputed Course already exists. Please choose a different one.",
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      className="md:col-span-2"
                    >
                      <Input
                        placeholder="Enter your course"
                        // onChange={(e) => updateStep1({ title: e.target.value })}
                      />
                    </Form.Item>
                  ) : null;
                }}
              </Form.Item>

            <Form.Item
              name="degree_id"
              label="Degree"
              rules={[requiredRule("Degree")]}
              validateTrigger="onBlur"
            >
              <Select
                allowClear
                showSearch
                placeholder="Select Degree"
                loading={isLoadingDegrees}
                options={degreeOptions}
              />
            </Form.Item>

            <Form.Item
              name="grade"
              label="Grade"
              rules={[requiredRule("Grade")]}
              validateTrigger="onBlur"
            >
              <Select
                allowClear
                showSearch
                placeholder="Select Grade"
                options={[
                  { value: "distinction", label: "Distinction" },
                  { value: "first_class", label: "First Class" },
                  { value: "second_upper", label: "Second Class Upper" },
                  { value: "second_lower", label: "Second Class Lower" },
                  { value: "third_class", label: "Third Class" },
                  { value: "pass", label: "Pass" },
                  { value: "fail", label: "Fail" },
                ]}
              />
            </Form.Item>

            {!currentSchool ? (
              <Form.Item
                name="date"
                label="Start & End Date"
                rules={[requiredRule("Start and End Date")]}
                validateTrigger="onBlur"
              >
                <DatePicker.RangePicker
                  className="w-full sm:w-auto"
                  format="MM/YYYY"
                  placeholder={["Start date", "End date"]}
                  disabledDate={disableFutureDates}
                  picker="month"
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="ss"
                label="Start Date"
                rules={[requiredRule("Start Date")]}
                validateTrigger="onBlur"
              >
                <DatePicker
                  picker="month"
                  className="w-full sm:w-auto"
                  format="MM/YYYY"
                  disabledDate={disableFutureDates}
                />
              </Form.Item>
            )}
          </div>
          <div className="mt-4">
            <AppButton
              type="submit"
              isLoading={isLoading}
              disabled={!isFormValid}
            />
          </div>
        </Form>
      </div>
    </Modal>
  );
};
